/* Nexus CRM — Authentication (Phase 7A: live backend integration)
   AuthProvider + useAuth hook + SignIn / ForgotPassword pages + AuthGate
   Tokens: access token in module-level var via apiClient; refresh token in localStorage. */
import { applyBrandingTokens } from '../shared/branding/index.js';

const { createContext: createCtxA, useContext: useCtxA, useState: useStateAU, useEffect: useEffectAU, useCallback: useCallbackAU } = React;

const SESSION_KEY = 'nx-session-v1';

/* ── Platform branding (fetched once, no-auth, drives all auth screens) ─── */
const PB_DEFAULTS = { brandName: 'URSA', logoUrl: null, primaryColor: '#125AB0', accentColor: '#4692EC', secondaryColor: '#e8f0fe', faviconUrl: null, loginBackground: '' };
let _pb = { ...PB_DEFAULTS };
let _pbFetched = false;

const applyPlatformBranding = (b) => {
  applyBrandingTokens(b);
  if (b.loginBackground) document.documentElement.style.setProperty('--nx-login-bg', `url(${b.loginBackground})`);
  if (b.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = b.faviconUrl;
  }
  if (b.brandName) document.title = b.brandName;
};

/* BrandMark — logo image (if set) or fallback icon + name. Used in every auth screen. */
const BrandMark = () => {
  const [brand, setBrand] = useStateAU(_pb);
  useEffectAU(() => {
    if (_pbFetched) return;
    _pbFetched = true;
    fetch('/api/v1/public/platform').then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      _pb = { ...PB_DEFAULTS, ...d };
      applyPlatformBranding(_pb);
      setBrand(_pb);
    }).catch(() => {});
  }, []);
  return (
    <div className="nx-auth-brand">
      {brand.logoUrl
        ? <img className="nx-auth-logo-img" src={brand.logoUrl} alt={brand.brandName} style={{ height: 20, objectFit: 'contain' }} />
        : <img className="nx-auth-logo-img" src="/ursa-logo.svg" alt="URSA" style={{ height: 20, objectFit: 'contain' }} />}
      <span className="nx-auth-brand-name">{brand.brandName}</span>
    </div>
  );
};

/* ── Token helpers (relay to apiClient exposed by _bridge.js) ──────────── */
const client = () => window.__nx_api?.client;
const setToken = (t)   => client()?.setToken(t);
const clearToken = ()  => client()?.clearToken();
const storeRt = (t)    => client()?.storeRefreshToken(t);
const clearRt = ()     => client()?.clearRefreshToken();
const getRt = ()       => client()?.getRefreshToken() || null;
/* SEC-F05: mark whether this login uses sessionStorage (non-remember-me) */
const setSessionOnly = (v) => client()?.setSessionOnly?.(v);

/* ── localStorage session cache (lightweight — for page-refresh restore) ─ */
const loadCached = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s.expiresAt || Date.now() > s.expiresAt) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch (_) { return null; }
};

const cacheSession = (s) => {
  try {
    /* expiresAt ties cache lifetime to refresh token (7 days); loadCached() checks this */
    localStorage.setItem(SESSION_KEY, JSON.stringify({ ...s, expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000 }));
  } catch { /* storage unavailable */ }
};

/* ── Fetch helpers (direct fetch — apiClient may not be ready at module parse time) ── */
const apiFetch = async (path, opts = {}) => {
  const headers = { 'Content-Type': 'application/json', ...opts.headers };
  const token = client()?.getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`/api/v1${path}`, { ...opts, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(Array.isArray(body.message) ? body.message.join('; ') : (body.message || `HTTP ${res.status}`));
  }
  if (res.status === 204) return null;
  return res.json();
};

/* ── Build a session object from login/refresh response + session context ─ */
const buildSession = (authData, ctx, cached = null) => ({
  id:                   authData.user.id,
  email:                authData.user.email,
  name:                 authData.user.name,
  platformRole:         authData.user.platformRole,
  avatarTone:           cached?.avatarTone || '#125AB0',
  activeOrgId:          ctx?.activeOrgId          || null,
  activeOrgName:        ctx?.activeOrgName         || null,
  activeWorkspaceId:    ctx?.activeWorkspaceId     || null,
  activeWorkspaceName:  ctx?.activeWorkspaceName   || null,
  effectiveRole:        ctx?.effectiveRole         || authData.user.platformRole,
  capabilities:         ctx?.capabilities          || [],
  enabledModules:       ctx?.enabledModules        || [],
  contexts:             authData.contexts           || [],
  signedInAt:           cached?.signedInAt         || Date.now(),
  expiresAt:            cached?.expiresAt          || (Date.now() + 30 * 24 * 60 * 60 * 1000),
});

/* ── AuthContext ─────────────────────────────────────────────────────────── */
const AuthCtx = createCtxA(null);

const AuthProvider = ({ children }) => {
  const [user, setUser]       = useStateAU(null);
  const [loading, setLoading] = useStateAU(true); /* true while restoring session */

  /* ── Session restore on mount ─────────────────────────────────────────── */
  useEffectAU(() => {
    const restore = async () => {
      const rt = getRt();
      if (!rt) { setLoading(false); return; }

      try {
        const refreshData = await apiFetch('/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refreshToken: rt }),
        });
        setToken(refreshData.accessToken);
        storeRt(refreshData.refreshToken);

        const [ctx, meData] = await Promise.all([
          apiFetch('/session/context').catch(() => null),
          apiFetch('/auth/me').catch(() => null),
        ]);
        const cached = loadCached();
        const session = buildSession({ ...refreshData, contexts: meData?.contexts || [] }, ctx, cached);

        cacheSession(session);
        setUser(session);
        window.__nx_auth = { user: session };
        window.dispatchEvent(new CustomEvent('nx-session-changed', { detail: { user: session } }));
      } catch (_) {
        /* Refresh token expired or backend unreachable — clear and show login */
        clearToken();
        clearRt();
        localStorage.removeItem(SESSION_KEY);
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, []);

  /* ── Listen for session-expired events from apiClient ────────────────── */
  useEffectAU(() => {
    const onExpired = () => {
      clearToken();
      clearRt();
      localStorage.removeItem(SESSION_KEY);
      setUser(null);
      window.__nx_auth = { user: null };
      window.dispatchEvent(new CustomEvent('nx-session-changed', { detail: { user: null } }));
    };
    window.addEventListener('nx-session-expired', onExpired);
    return () => window.removeEventListener('nx-session-expired', onExpired);
  }, []);

  /* AUTH-F-001: cross-tab logout — when another tab clears the refresh token,
     sign out this tab too (storage events don't fire in the originating tab) */
  useEffectAU(() => {
    const onStorage = (e) => {
      if (e.key === 'nx-refresh-token' && e.newValue === null) {
        clearToken();
        localStorage.removeItem(SESSION_KEY);
        setUser(null);
        globalThis.__nx_auth = { user: null };
        globalThis.dispatchEvent(new CustomEvent('nx-session-changed', { detail: { user: null } }));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /* ── signIn ──────────────────────────────────────────────────────────── */
  const signIn = useCallbackAU(async ({ email, password, remember }) => {
    setLoading(true);
    try {
      const loginData = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, remember }),
      });
      setToken(loginData.accessToken);
      setSessionOnly(!remember);
      storeRt(loginData.refreshToken);

      const [ctx, meData] = await Promise.all([
        apiFetch('/session/context').catch(() => null),
        apiFetch('/auth/me').catch(() => null),
      ]);
      const expiresAt = Date.now() + (remember ? 30 : 1) * 24 * 60 * 60 * 1000;
      const session = buildSession(
        { ...loginData, contexts: meData?.contexts || [] },
        ctx,
        { avatarTone: '#125AB0', signedInAt: Date.now(), expiresAt }
      );

      cacheSession(session);
      setUser(session);
      window.__nx_auth = { user: session };
      window.dispatchEvent(new CustomEvent('nx-session-changed', { detail: { user: session } }));
      return session;
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── signOut ─────────────────────────────────────────────────────────── */
  const signOut = useCallbackAU(async () => {
    const rt = getRt();
    try { if (rt) await apiFetch('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken: rt }) }); } catch (_) {}

    clearToken();
    clearRt();
    localStorage.removeItem(SESSION_KEY);

    try {
      sessionStorage.removeItem('nx-deep-link');
      ['people', 'companies', 'deals', 'tasks'].forEach(m => {
        sessionStorage.removeItem('nx-filters-' + m);
        sessionStorage.removeItem('nx-filters-' + m + '-search');
      });
    } catch (_) {}

    setUser(null);
    window.__nx_auth = { user: null };
    window.dispatchEvent(new CustomEvent('nx-session-changed', { detail: { user: null } }));
  }, []);

  /* ── forgotPassword ──────────────────────────────────────────────────── */
  const requestReset = useCallbackAU(async (email) => {
    await apiFetch('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    /* Backend sends the email; we don't get a token back in production */
    return null;
  }, []);

  /* ── resetPassword (used by ResetPasswordPage via token in URL) ───────── */
  const resetPassword = useCallbackAU(async (token, newPassword) => {
    await apiFetch('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }, []);

  /* ── switchOrg / switchWorkspace (for WorkspaceSwitcher) ─────────────── */
  const switchOrg = useCallbackAU(async (orgId) => {
    await apiFetch('/session/context/org', { method: 'PUT', body: JSON.stringify({ orgId }) });
    const [ctx, meData] = await Promise.all([
      apiFetch('/session/context').catch(() => null),
      apiFetch('/auth/me').catch(() => null),
    ]);
    setUser(s => {
      const next = { ...s,
        activeOrgId:           ctx?.activeOrgId           ?? s.activeOrgId,
        activeOrgName:         ctx?.activeOrgName         ?? s.activeOrgName,
        activeWorkspaceId:     ctx?.activeWorkspaceId     ?? s.activeWorkspaceId,
        activeWorkspaceName:   ctx?.activeWorkspaceName   ?? s.activeWorkspaceName,
        effectiveRole:         ctx?.effectiveRole         ?? s.effectiveRole,
        capabilities:          ctx?.capabilities          ?? s.capabilities,
        enabledModules:        ctx?.enabledModules        ?? s.enabledModules,
        contexts:              meData?.contexts           ?? s.contexts,
      };
      cacheSession(next);
      window.__nx_auth = { user: next };
      window.dispatchEvent(new CustomEvent('nx-session-changed', { detail: { user: next } }));
      return next;
    });
  }, []);

  const switchWorkspace = useCallbackAU(async (workspaceId) => {
    await apiFetch('/session/context/workspace', { method: 'PUT', body: JSON.stringify({ workspaceId }) });
    const [ctx, meData] = await Promise.all([
      apiFetch('/session/context').catch(() => null),
      apiFetch('/auth/me').catch(() => null),
    ]);
    setUser(s => {
      const next = { ...s,
        activeWorkspaceId:     ctx?.activeWorkspaceId     ?? s.activeWorkspaceId,
        activeWorkspaceName:   ctx?.activeWorkspaceName   ?? s.activeWorkspaceName,
        effectiveRole:         ctx?.effectiveRole         ?? s.effectiveRole,
        capabilities:          ctx?.capabilities          ?? s.capabilities,
        enabledModules:        ctx?.enabledModules        ?? s.enabledModules,
        contexts:              meData?.contexts           ?? s.contexts,
      };
      cacheSession(next);
      window.__nx_auth = { user: next };
      window.dispatchEvent(new CustomEvent('nx-session-changed', { detail: { user: next } }));
      return next;
    });
  }, []);

  const value = { user, loading, signIn, signOut, requestReset, resetPassword, switchOrg, switchWorkspace };
  /* SEC-003: expose only read-only user data on window; auth functions via window.useAuth() */
  window.__nx_auth = { user: value.user };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

const useAuth = () => {
  const ctx = useCtxA(AuthCtx);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

/* SEC-001: make these non-writable/non-configurable so malicious scripts cannot
   overwrite them to spoof a SUPER_ADMIN session (FINDING-01 remediation). */
Object.defineProperty(window, 'AuthProvider', { value: AuthProvider, writable: false, configurable: false });
Object.defineProperty(window, 'useAuth',      { value: useAuth,      writable: false, configurable: false });

/* =========================================================
   AuthGate — renders loading spinner or SignIn until authenticated
   ========================================================= */
const AuthGate = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <AuthLayout>
        <div className="nx-auth-card" style={{ textAlign: 'center', padding: '2rem' }}>
          <BrandMark />
          <p style={{ color: 'var(--nx-muted)', marginTop: '1.5rem', fontSize: '0.85rem' }}>Restoring session…</p>
        </div>
      </AuthLayout>
    );
  }

  if (!user) return <SignInPage />;
  return children;
};
/* =========================================================
   SignInPage
   ========================================================= */
const SignInPage = () => {
  const { signIn, requestReset, loading } = useAuth();
  const [mode, setMode]         = useStateAU('signin');
  const [email, setEmail]       = useStateAU('');
  const [password, setPassword] = useStateAU('');
  const [remember, setRemember] = useStateAU(true);
  const [error, setError]       = useStateAU('');
  const [busy, setBusy]         = useStateAU(false);

  const submit = async (e) => {
    e && e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await signIn({ email, password, remember });
    } catch (err) {
      setError(err.message || 'Sign-in failed. Check your credentials.');
    } finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e && e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await requestReset(email);
      setMode('sent');
    } catch (err) {
      setError(err.message || 'Could not send reset link.');
    } finally { setBusy(false); }
  };

  return (
    <AuthLayout>
      <div className="nx-auth-card">
        <BrandMark />

        {mode === 'signin' && (
          <form className="nx-auth-form" onSubmit={submit}>
            <h1 className="nx-auth-title">Welcome back</h1>
            <p className="nx-auth-sub">Sign in to your workspace to continue.</p>

            <label className="nx-auth-label">Email</label>
            <input className="nx-input nx-auth-input" type="email" autoFocus required value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />

            <label className="nx-auth-label">Password</label>
            <input className="nx-input nx-auth-input" type="password" required minLength={6} value={password}
              onChange={e => setPassword(e.target.value)} placeholder="••••••••" />

            <div className="nx-auth-row">
              <label className="nx-auth-check">
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                <span>Remember me for 30 days</span>
              </label>
              <button type="button" className="nx-auth-link"
                onClick={() => { setMode('forgot'); setError(''); }}>Forgot password?</button>
            </div>

            {error && <div className="nx-auth-error"><NexusIcon name="AlertCircle" size={12} /> {error}</div>}

            <Btn variant="primary" icon="ArrowRight" className="nx-auth-submit"
              disabled={busy || loading} onClick={submit}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Btn>
          </form>
        )}

        {mode === 'forgot' && (
          <form className="nx-auth-form" onSubmit={submitForgot}>
            <h1 className="nx-auth-title">Reset your password</h1>
            <p className="nx-auth-sub">Enter your email and we'll send a reset link.</p>

            <label className="nx-auth-label">Email</label>
            <input className="nx-input nx-auth-input" type="email" autoFocus required value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />

            {error && <div className="nx-auth-error"><NexusIcon name="AlertCircle" size={12} /> {error}</div>}

            <Btn variant="primary" icon="Mail" className="nx-auth-submit"
              disabled={busy} onClick={submitForgot}>
              {busy ? 'Sending…' : 'Send reset link'}
            </Btn>
            <button type="button" className="nx-auth-link nx-auth-back"
              onClick={() => { setMode('signin'); setError(''); }}>
              <NexusIcon name="ArrowLeft" size={11} /> Back to sign in
            </button>
          </form>
        )}

        {mode === 'sent' && (
          <div className="nx-auth-form">
            <div className="nx-auth-success-icon"><NexusIcon name="Check" size={20} /></div>
            <h1 className="nx-auth-title">Check your inbox</h1>
            <p className="nx-auth-sub">
              A reset link has been sent to <strong>{email}</strong>. It will expire in 30 minutes.
            </p>
            <Btn variant="primary" icon="ArrowLeft" className="nx-auth-submit"
              onClick={() => { setMode('signin'); setError(''); }}>
              Back to sign in
            </Btn>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};
window.SignInPage = SignInPage;

/* =========================================================
   ResetPasswordPage — reached via /reset-password/:token link in email.
   token is extracted from URL params by a wrapper in App.jsx and passed as prop.
   Calls POST /auth/reset-password. No auth context needed.
   ========================================================= */
const ResetPasswordPage = ({ token }) => {
  const [password, setPassword] = useStateAU('');
  const [confirm, setConfirm]   = useStateAU('');
  const [error, setError]       = useStateAU('');
  const [done, setDone]         = useStateAU(false);
  const [busy, setBusy]         = useStateAU(false);

  const submit = async (e) => {
    e && e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    setError('');
    setBusy(true);
    try {
      await apiFetch('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: password }),
      });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Reset failed. The link may have expired or already been used.');
    } finally { setBusy(false); }
  };

  return (
    <AuthLayout>
      <div className="nx-auth-card">
        <BrandMark />
        {done ? (
          <div className="nx-auth-form">
            <div className="nx-auth-success-icon"><NexusIcon name="Check" size={20} /></div>
            <h1 className="nx-auth-title">Password updated</h1>
            <p className="nx-auth-sub">Your password has been reset. You can now sign in with your new password.</p>
            <Btn variant="primary" icon="ArrowRight" className="nx-auth-submit"
              onClick={() => { window.location.href = '/'; }}>
              Go to sign in
            </Btn>
          </div>
        ) : (
          <form className="nx-auth-form" onSubmit={submit}>
            <h1 className="nx-auth-title">Set new password</h1>
            <p className="nx-auth-sub">Choose a strong password for your account.</p>

            <label className="nx-auth-label">New password</label>
            <input className="nx-input nx-auth-input" type="password" autoFocus required minLength={8}
              value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />

            <label className="nx-auth-label">Confirm password</label>
            <input className="nx-input nx-auth-input" type="password" required minLength={8}
              value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="••••••••" />

            {error && <div className="nx-auth-error"><NexusIcon name="AlertCircle" size={12} /> {error}</div>}

            <Btn variant="primary" icon="Check" className="nx-auth-submit"
              disabled={busy} onClick={submit}>
              {busy ? 'Saving…' : 'Set password'}
            </Btn>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};
window.ResetPasswordPage = ResetPasswordPage;

/* =========================================================
   InviteAcceptPage — reached via /invite/:token link in invitation email.
   token is passed as prop by App.jsx wrapper.
   Validates token first (GET /auth/invite/:token/validate) to surface org
   name, then accepts (POST /auth/invite/accept) to activate the account.
   ========================================================= */
const InviteAcceptPage = ({ token }) => {
  const [info, setInfo]         = useStateAU(null);
  const [loading, setLoading]   = useStateAU(true);
  const [name, setName]         = useStateAU('');
  const [password, setPassword] = useStateAU('');
  const [confirm, setConfirm]   = useStateAU('');
  const [error, setError]       = useStateAU('');
  const [done, setDone]         = useStateAU(false);
  const [busy, setBusy]         = useStateAU(false);

  React.useEffect(() => {
    apiFetch(`/auth/invite/${token}/validate`)
      .then(data => setInfo(data))
      .catch(err => setError(err.message || 'This invitation link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e && e.preventDefault();
    if (!name.trim())            { setError('Please enter your name.'); return; }
    if (password !== confirm)    { setError('Passwords do not match.'); return; }
    if (password.length < 8)     { setError('Password must be at least 8 characters.'); return; }
    setError('');
    setBusy(true);
    try {
      await apiFetch('/auth/invite/accept', {
        method: 'POST',
        body: JSON.stringify({ token, name: name.trim(), password }),
      });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Invitation acceptance failed. The link may have expired.');
    } finally { setBusy(false); }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="nx-auth-form" style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>
          Verifying invitation…
        </div>
      );
    }
    if (done) {
      return (
        <div className="nx-auth-form">
          <div className="nx-auth-success-icon"><NexusIcon name="Check" size={20} /></div>
          <h1 className="nx-auth-title">Account created</h1>
          <p className="nx-auth-sub">You're all set. Sign in to access your workspace.</p>
          <Btn variant="primary" icon="ArrowRight" className="nx-auth-submit"
            onClick={() => { window.location.href = '/'; }}>
            Sign in
          </Btn>
        </div>
      );
    }
    if (!info) {
      return (
        <div className="nx-auth-form">
          <h1 className="nx-auth-title">Invalid invitation</h1>
          <div className="nx-auth-error" style={{ marginTop: 8 }}>
            <NexusIcon name="AlertCircle" size={12} /> {error || 'This invitation link is invalid or has expired.'}
          </div>
        </div>
      );
    }
    return (
      <form className="nx-auth-form" onSubmit={submit}>
        <h1 className="nx-auth-title">Accept invitation</h1>
        <p className="nx-auth-sub">
          You've been invited to join <strong>{info.orgName}</strong>.
          Complete your account to get started.
        </p>

        {info.email && (
          <>
            <label className="nx-auth-label" htmlFor="invite-email">Email</label>
            <input id="invite-email" className="nx-input nx-auth-input" type="email" value={info.email} disabled
              style={{ opacity: 0.7, cursor: 'not-allowed' }} />
          </>
        )}

        <label className="nx-auth-label" htmlFor="invite-name">Your name</label>
        <input id="invite-name" className="nx-input nx-auth-input" type="text" autoFocus required
          value={name} onChange={e => setName(e.target.value)} placeholder="First Last" />

        <label className="nx-auth-label" htmlFor="invite-password">Choose a password</label>
        <input id="invite-password" className="nx-input nx-auth-input" type="password" required minLength={8}
          value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />

        <label className="nx-auth-label" htmlFor="invite-confirm">Confirm password</label>
        <input id="invite-confirm" className="nx-input nx-auth-input" type="password" required minLength={8}
          value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="••••••••" />

        {error && <div className="nx-auth-error"><NexusIcon name="AlertCircle" size={12} /> {error}</div>}

        <Btn variant="primary" icon="UserPlus" className="nx-auth-submit"
          disabled={busy} onClick={submit}>
          {busy ? 'Creating account…' : 'Create account'}
        </Btn>
      </form>
    );
  };

  return (
    <AuthLayout>
      <div className="nx-auth-card">
        <BrandMark />
        {renderBody()}
      </div>
    </AuthLayout>
  );
};
window.InviteAcceptPage = InviteAcceptPage;

/* =========================================================
   PlatformInviteAcceptPage — reached via /platform-invite/:token
   Calls GET /auth/platform-invite/:token/validate then
   POST /auth/platform-invite/accept to create the SA account.
   ========================================================= */
const PlatformInviteAcceptPage = ({ token }) => {
  const [info, setInfo]         = useStateAU(null);
  const [name, setName]         = useStateAU('');
  const [password, setPassword] = useStateAU('');
  const [confirm, setConfirm]   = useStateAU('');
  const [error, setError]       = useStateAU('');
  const [done, setDone]         = useStateAU(false);
  const [busy, setBusy]         = useStateAU(false);
  const [validating, setValidating] = useStateAU(true);

  useEffectAU(() => {
    apiFetch(`/auth/platform-invite/${token}/validate`)
      .then(d => { setInfo(d); if (d.name) setName(d.name); })
      .catch(() => setError('This invitation link is invalid or has expired.'))
      .finally(() => setValidating(false));
  }, [token]);

  const submit = async (e) => {
    e && e.preventDefault();
    if (!name.trim())            { setError('Please enter your name.'); return; }
    if (password !== confirm)    { setError('Passwords do not match.'); return; }
    if (password.length < 8)     { setError('Password must be at least 8 characters.'); return; }
    setError('');
    setBusy(true);
    try {
      await apiFetch('/auth/platform-invite/accept', {
        method: 'POST',
        body: JSON.stringify({ token, name: name.trim(), password }),
      });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Invitation acceptance failed. The link may have expired.');
    } finally { setBusy(false); }
  };

  return (
    <AuthLayout>
      <div className="nx-auth-card">
        <BrandMark />
        {validating ? (
          <div className="nx-auth-form" style={{ textAlign: 'center', padding: '20px 0' }}>
            <NexusIcon name="Loader" size={20} />
            <p className="nx-auth-sub" style={{ marginTop: 8 }}>Validating invitation…</p>
          </div>
        ) : done ? (
          <div className="nx-auth-form">
            <div className="nx-auth-success-icon"><NexusIcon name="Check" size={20} /></div>
            <h1 className="nx-auth-title">Account created</h1>
            <p className="nx-auth-sub">Your Control Center account is ready. Sign in to get started.</p>
            <Btn variant="primary" icon="ArrowRight" className="nx-auth-submit"
              onClick={() => { window.location.href = '/'; }}>
              Sign in to Control Center
            </Btn>
          </div>
        ) : error && !info ? (
          <div className="nx-auth-form">
            <div className="nx-auth-error" style={{ marginTop: 8 }}><NexusIcon name="AlertCircle" size={12} /> {error}</div>
            <Btn variant="ghost" icon="ArrowLeft" className="nx-auth-submit" style={{ marginTop: 16 }}
              onClick={() => { window.location.href = '/'; }}>
              Back to sign in
            </Btn>
          </div>
        ) : (
          <form className="nx-auth-form" onSubmit={submit}>
            <h1 className="nx-auth-title">Accept platform invitation</h1>
            <p className="nx-auth-sub">
              You've been invited as <strong>{info?.role?.replace(/_/g, ' ') || 'Platform Admin'}</strong> on URSA Control Center.
              {info?.email && <><br /><span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{info.email}</span></>}
            </p>

            <label className="nx-auth-label">Your name</label>
            <input className="nx-input nx-auth-input" type="text" autoFocus required
              value={name} onChange={e => setName(e.target.value)} placeholder="First Last" />

            <label className="nx-auth-label">Choose a password</label>
            <input className="nx-input nx-auth-input" type="password" required minLength={8}
              value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" />

            <label className="nx-auth-label">Confirm password</label>
            <input className="nx-input nx-auth-input" type="password" required minLength={8}
              value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="••••••••" />

            {error && <div className="nx-auth-error"><NexusIcon name="AlertCircle" size={12} /> {error}</div>}

            <Btn variant="primary" icon="ShieldCheck" className="nx-auth-submit"
              disabled={busy} onClick={submit}>
              {busy ? 'Creating account…' : 'Create Control Center account'}
            </Btn>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};
window.PlatformInviteAcceptPage = PlatformInviteAcceptPage;
